import { useEffect, useRef, useState, type ReactNode } from 'react';
import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, PLATFORMS, platformMeta } from '../data/sample';
import { state$, goExplorer, goAgent, goLogin, openNiche, selectPlatform } from '../state';

interface Crumb {
  label: string;
  onClick: () => void;
  color: string;
  weight: number;
  sep: string;
}

/** Square marketplace badge (letter on a brand-accent fill). */
function PlatformBadge({
  slug,
  size = 16,
}: {
  slug: (typeof PLATFORMS)[number]['slug'];
  size?: number;
}) {
  const p = platformMeta(slug);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size <= 20 ? 5 : 7,
        background: p.badgeBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 800,
        fontSize: size * 0.62,
        fontFamily: "'JetBrains Mono'",
        flexShrink: 0,
      }}
    >
      {p.initial}
    </div>
  );
}

/** Top-bar marketplace switcher — filters the Explorer by platform. */
const PlatformSwitcher = observer(function PlatformSwitcher() {
  const slug = use$(state$.platformSlug);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = platformMeta(slug);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          height: 34,
          padding: '0 10px',
          background: open ? '#f1f3f5' : '#fff',
          border: '1px solid #e1e3e5',
          borderRadius: 9,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <PlatformBadge slug={slug} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{current.name}</span>
        <span style={{ fontSize: 11, color: '#9aa0ab' }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            width: 268,
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 13,
            boxShadow: '0 24px 60px rgba(26,29,36,0.14)',
            padding: 7,
            zIndex: 60,
            animation: 'nl-rise .16s ease',
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.07em',
              color: '#9aa0ab',
              textTransform: 'uppercase',
              padding: '7px 10px 8px',
            }}
          >
            Marketplace
          </div>
          {PLATFORMS.map((p) => {
            const on = p.slug === slug;
            return (
              <button
                key={p.slug}
                onClick={() => {
                  setOpen(false);
                  if (!on) selectPlatform(p.slug);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  width: '100%',
                  padding: '8px 10px',
                  background: on ? '#f4f6f8' : 'none',
                  border: 'none',
                  borderRadius: 9,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <PlatformBadge slug={p.slug} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#9aa0ab' }}>{p.tagline}</div>
                </div>
                {on && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: C.primary,
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

const TopBar = observer(function TopBar() {
  const search = use$(state$.search);
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        height: 60,
        padding: '0 24px',
        background: 'rgba(255,255,255,0.86)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #e1e3e5',
      }}
    >
      <div
        onClick={goExplorer}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'linear-gradient(135deg,#2c6ecb,#1f5199)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(44,110,203,0.35)',
          }}
        >
          <div style={{ width: 12, height: 12, border: '2.5px solid #fff', borderRadius: '50%' }} />
        </div>
        <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>ScanGap</span>
      </div>
      <PlatformSwitcher />
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 440 }}>
          <span
            style={{
              position: 'absolute',
              left: 13,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#aab0bb',
              fontSize: 14,
            }}
          >
            ⌕
          </span>
          <input
            value={search}
            onChange={(e) => state$.search.set(e.target.value)}
            placeholder="Search niches, apps, or functions…"
            style={{
              width: '100%',
              height: 38,
              padding: '0 14px 0 34px',
              border: '1px solid #e1e3e5',
              borderRadius: 10,
              background: '#fff',
              fontFamily: 'inherit',
              fontSize: 13.5,
              color: '#303030',
              outline: 'none',
            }}
          />
        </div>
      </div>
      <button
        onClick={goAgent}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          height: 38,
          padding: '0 14px',
          background: '#fff',
          border: '1px solid #e1e3e5',
          borderRadius: 10,
          fontFamily: "'JetBrains Mono'",
          fontSize: 12.5,
          fontWeight: 600,
          color: '#616161',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <span>{'{ }'}</span> Agent / MCP
      </button>
      <div
        onClick={goLogin}
        title="Log out"
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: 'linear-gradient(135deg,#2c6ecb,#1f5199)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
          cursor: 'pointer',
        }}
      >
        JD
      </div>
    </header>
  );
});

const Breadcrumb = observer(function Breadcrumb() {
  const view = use$(state$.view);
  const platformSlug = use$(state$.platformSlug);
  const nicheId = use$(state$.nicheId);
  const funcId = use$(state$.funcId);
  const appId = use$(state$.appId);

  const crumbs: Crumb[] = [
    {
      label: platformMeta(platformSlug).name,
      onClick: goExplorer,
      color: C.ink3,
      weight: 600,
      sep: '›',
    },
  ];
  const niche = SAMPLE.niches.find((n) => n.id === nicheId);
  if (view === 'niche' && niche) {
    crumbs.push({ label: niche.name, onClick: () => {}, color: C.ink, weight: 700, sep: '' });
  }
  if (view === 'function') {
    const f = SAMPLE.functions.find((x) => x.id === funcId);
    crumbs.push({
      label: 'Reviews & UGC',
      onClick: () => openNiche('reviews-ugc'),
      color: C.ink3,
      weight: 600,
      sep: '›',
    });
    crumbs.push({ label: f ? f.name : '', onClick: () => {}, color: C.ink, weight: 700, sep: '' });
  }
  if (view === 'app') {
    const a = SAMPLE.apps.find((x) => x.id === appId);
    crumbs.push({
      label: 'Reviews & UGC',
      onClick: () => openNiche('reviews-ugc'),
      color: C.ink3,
      weight: 600,
      sep: '›',
    });
    crumbs.push({ label: a ? a.name : '', onClick: () => {}, color: C.ink, weight: 700, sep: '' });
  }
  if (view === 'agent') {
    crumbs.push({ label: 'Agent / MCP', onClick: () => {}, color: C.ink, weight: 700, sep: '' });
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '11px 26px 0',
        fontSize: 13,
        color: '#6d7175',
      }}
    >
      {crumbs.map((c, i) => (
        <span key={i} style={{ display: 'contents' }}>
          <span
            onClick={c.onClick}
            style={{ cursor: 'pointer', color: c.color, fontWeight: c.weight }}
          >
            {c.label}
          </span>
          <span style={{ color: '#cdd1d9' }}>{c.sep}</span>
        </span>
      ))}
      <div style={{ flex: 1 }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 9px',
          background: '#fff5ea',
          border: '1px solid #ffd79d',
          borderRadius: 7,
          fontSize: 11,
          fontWeight: 600,
          color: '#8a6116',
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#b98900' }} />{' '}
        Illustrative sample data
      </div>
    </div>
  );
});

export function Chrome({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <TopBar />
      <Breadcrumb />
      <main style={{ flex: 1, padding: '18px 26px 60px' }}>{children}</main>
    </div>
  );
}
